import { useState, useEffect } from 'react'
import { Modal, ModalFooter, Button, Input, Alert } from '@orientamos/ui'
import { Mail, Plus, X, Send } from 'lucide-react'
import { sendContractEmail, type ContractDetail } from '@lib/api'

interface SendContractModalProps {
  open: boolean
  onClose: () => void
  contract: ContractDetail
  onSuccess: () => void
}

/**
 * Modal for sending contracts via email
 * Pre-fills the recipient with the tenant's email from the contract data snapshot
 */
export function SendContractModal({ open, onClose, contract, onSuccess }: SendContractModalProps) {
  // Extract tenant email from contract data snapshot
  const getTenantEmail = (): string => {
    const snapshot = contract.dataSnapshot as {
      parties?: Array<{
        role: string
        email: string
        firstName: string
        lastName: string
      }>
    }
    const tenant = snapshot?.parties?.find((p) => p.role === 'tenant')
    return tenant?.email || ''
  }

  const getTenantName = (): string => {
    const snapshot = contract.dataSnapshot as {
      parties?: Array<{
        role: string
        firstName: string
        lastName: string
      }>
    }
    const tenant = snapshot?.parties?.find((p) => p.role === 'tenant')
    return tenant ? `${tenant.firstName} ${tenant.lastName}` : ''
  }

  // Form state
  const [toEmails, setToEmails] = useState<string[]>([])
  const [newToEmail, setNewToEmail] = useState('')
  const [ccEmails, setCcEmails] = useState<string[]>([])
  const [newCcEmail, setNewCcEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  // UI state
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toError, setToError] = useState<string | null>(null)
  const [ccError, setCcError] = useState<string | null>(null)

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      const tenantEmail = getTenantEmail()
      setToEmails(tenantEmail ? [tenantEmail] : [])
      setCcEmails([])
      setNewToEmail('')
      setNewCcEmail('')
      setSubject(`Contrato de Arrendamiento - ${contract.orderNumber}`)
      setMessage('')
      setError(null)
      setToError(null)
      setCcError(null)
    }
  }, [open, contract])

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
  }

  // Add email to To list
  const handleAddToEmail = () => {
    const email = newToEmail.trim()
    if (!email) return

    if (!isValidEmail(email)) {
      setToError('Email invalido')
      return
    }

    if (toEmails.includes(email)) {
      setToError('Este email ya esta en la lista')
      return
    }

    setToEmails([...toEmails, email])
    setNewToEmail('')
    setToError(null)
  }

  // Remove email from To list
  const handleRemoveToEmail = (email: string) => {
    setToEmails(toEmails.filter((e) => e !== email))
  }

  // Add email to CC list
  const handleAddCcEmail = () => {
    const email = newCcEmail.trim()
    if (!email) return

    if (!isValidEmail(email)) {
      setCcError('Email invalido')
      return
    }

    if (ccEmails.includes(email) || toEmails.includes(email)) {
      setCcError('Este email ya esta en la lista')
      return
    }

    setCcEmails([...ccEmails, email])
    setNewCcEmail('')
    setCcError(null)
  }

  // Remove email from CC list
  const handleRemoveCcEmail = (email: string) => {
    setCcEmails(ccEmails.filter((e) => e !== email))
  }

  // Handle key press for adding emails
  const handleToKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddToEmail()
    }
  }

  const handleCcKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddCcEmail()
    }
  }

  // Submit handler
  const handleSubmit = async () => {
    // Validate
    if (toEmails.length === 0) {
      setError('Se requiere al menos un destinatario')
      return
    }

    if (!subject.trim()) {
      setError('El asunto es requerido')
      return
    }

    setIsSending(true)
    setError(null)

    try {
      const result = await sendContractEmail(contract.id, {
        to: toEmails,
        cc: ccEmails.length > 0 ? ccEmails : undefined,
        subject: subject.trim(),
        message: message.trim() || undefined,
      })

      if (result.success) {
        onSuccess()
        onClose()
      } else {
        setError(result.error || 'Error al enviar el correo')
      }
    } catch (err) {
      console.error('Error sending email:', err)
      setError('Error de conexion al enviar el correo')
    } finally {
      setIsSending(false)
    }
  }

  const tenantName = getTenantName()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enviar Contrato por Correo"
      description={`Contrato ${contract.orderNumber}${tenantName ? ` - ${tenantName}` : ''}`}
      size="lg"
    >
      <div className="space-y-4">
        {error && <Alert variant="danger">{error}</Alert>}

        {/* To field */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
            Para <span className="text-danger-500">*</span>
          </label>

          {/* Email chips */}
          {toEmails.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {toEmails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-md text-sm"
                >
                  <Mail className="w-3 h-3" />
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveToEmail(email)}
                    className="ml-1 hover:text-primary-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Add email input */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Agregar destinatario..."
                value={newToEmail}
                onChange={(e) => {
                  setNewToEmail(e.target.value)
                  setToError(null)
                }}
                onKeyPress={handleToKeyPress}
                error={toError || undefined}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddToEmail}
              disabled={!newToEmail.trim()}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* CC field */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">CC (opcional)</label>

          {/* CC chips */}
          {ccEmails.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {ccEmails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 text-neutral-700 rounded-md text-sm"
                >
                  <Mail className="w-3 h-3" />
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveCcEmail(email)}
                    className="ml-1 hover:text-neutral-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Add CC input */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Agregar copia..."
                value={newCcEmail}
                onChange={(e) => {
                  setNewCcEmail(e.target.value)
                  setCcError(null)
                }}
                onKeyPress={handleCcKeyPress}
                error={ccError || undefined}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddCcEmail}
              disabled={!newCcEmail.trim()}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Subject field */}
        <Input
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Asunto del correo"
        />

        {/* Message field */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
            Mensaje adicional (opcional)
          </label>
          <textarea
            className="w-full h-32 px-3 py-2 rounded-lg border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500/20 resize-none"
            placeholder="Escriba aqui cualquier mensaje adicional que desee incluir en el correo..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={2000}
          />
          <p className="mt-1 text-xs text-neutral-500">{message.length}/2000 caracteres</p>
        </div>

        {/* Info box */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <p>
            <strong>Nota:</strong> El contrato sera enviado automaticamente como contenido del
            correo. Una vez enviado, el estado del contrato cambiara a "Enviado".
          </p>
        </div>
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={isSending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSending}
          disabled={toEmails.length === 0 || !subject.trim()}
        >
          <Send className="w-4 h-4 mr-1" />
          Enviar Correo
        </Button>
      </ModalFooter>
    </Modal>
  )
}
